
import { useState, useEffect } from 'react';

interface UserData {
  name: string;
  email: string;
  phoneNumber?: string;
  verified: boolean;
  onboardingCompleted: boolean;
  lastVerified?: string; // Optional timestamp for last verification
}

export const useOnboarding = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  const loadUserData = () => {
    console.log('useOnboarding - loadUserData called');
    setIsLoading(true);
    
    try {
      console.log('Loading user data from localStorage...');
      let storedData = localStorage.getItem('userData');
      
      // If no data in localStorage, try to recover from sessionStorage backup
      if (!storedData) {
        console.log('No data in localStorage, checking sessionStorage backup...');
        const backupData = sessionStorage.getItem('userData_backup');
        if (backupData) {
          console.log('Found backup data, restoring to localStorage...');
          localStorage.setItem('userData', backupData);
          storedData = backupData;
        }
      }
      
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData) as UserData;
          console.log('Successfully loaded user data:', parsedData);
          
          setUserData(parsedData);
          setOnboardingComplete(parsedData.onboardingCompleted === true);
        } catch (error) {
          console.error('Error parsing user data:', error);
          // Clear invalid data
          localStorage.removeItem('userData');
          sessionStorage.removeItem('userData_backup');
          setUserData(null);
          setOnboardingComplete(false);
        }
      } else {
        console.log('No user data found in localStorage or sessionStorage');
        // No stored data means first visit
        setUserData(null);
        setOnboardingComplete(false);
      }
    } catch (error) {
      console.error('Error in loadUserData:', error);
      // Handle any unexpected errors
      setUserData(null);
      setOnboardingComplete(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Use useEffect properly to ensure it runs after component mount
  useEffect(() => {
    console.log('useOnboarding - useEffect triggered, loading user data');
    
    // Add a small delay to ensure React is fully initialized
    const timeoutId = setTimeout(() => {
      loadUserData();
    }, 0);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const saveUserData = (data: Partial<UserData>) => {
    try {
      const updatedData = {...(userData || {}), ...data} as UserData;
      console.log('Saving user data:', updatedData);
      
      // Save to localStorage
      localStorage.setItem('userData', JSON.stringify(updatedData));
      
      // Create backup in sessionStorage
      sessionStorage.setItem('userData_backup', JSON.stringify(updatedData));
      
      setUserData(updatedData);
      
      if (data.onboardingCompleted !== undefined) {
        setOnboardingComplete(data.onboardingCompleted);
        
        // If onboarding just completed, trigger data sync
        if (data.onboardingCompleted && updatedData.email) {
          console.log('🎮 Onboarding completed - triggering data sync for:', updatedData.email);
          // Dispatch a custom event to trigger sync
          window.dispatchEvent(new CustomEvent('onboardingCompleted', { 
            detail: { userData: updatedData } 
          }));
        }
      }
    } catch (error) {
      console.error('Error saving user data:', error);
    }
  };

  const resetOnboarding = () => {
    try {
      console.log('Resetting onboarding data');
      localStorage.removeItem('userData');
      sessionStorage.removeItem('userData_backup');
      setUserData(null);
      setOnboardingComplete(false);
    } catch (error) {
      console.error('Error resetting onboarding:', error);
    }
  };

  return {
    isLoading,
    userData,
    onboardingComplete,
    resetOnboarding,
    loadUserData,
    saveUserData
  };
};
